<template>
  <div>
    <div class="row g-3 mb-3">
      <div class="col-md-7">
        <label class="form-label small fw-semibold">Nom de la catégorie *</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.name"
          required
        />
      </div>
      <div class="col-md-5">
        <label class="form-label small fw-semibold">Code</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.code"
          placeholder="Ex : MEDI, PARA, DIGI..."
        />
        <small class="text-muted">
          Laisser vide pour générer automatiquement.
        </small>
      </div>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-4">
        <label class="form-label small fw-semibold">Position</label>
        <PrimevueInputNumber
          class="w-100"
          :min="0"
          v-model="form.position"
        />
        <small class="text-muted">
          Ordre dans les listes (0 = par défaut).
        </small>
      </div>
      <div class="col-md-8">
        <label class="form-label small fw-semibold">Actif</label>
        <div class="form-check d-flex align-items-center gap-2 ps-0">
          <PrimevueCheckbox v-model="form.is_active" binary inputId="categoryActive" />
          <label class="form-check-label small" for="categoryActive">
            Utilisable pour les produits
          </label>
        </div>
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label small fw-semibold">Image de la catégorie</label>
      <div class="category-image-field">
        <div class="category-image-preview">
          <img
            v-if="imagePreviewUrl || form.image_url"
            :src="imagePreviewUrl || form.image_url"
            alt="Aperçu catégorie"
          />
          <i v-else class="fa fa-image text-muted"></i>
        </div>
        <div class="flex-grow-1">
          <input
            ref="imageInput"
            type="file"
            class="form-control form-control-sm"
            accept="image/jpeg,image/png,image/webp"
            @change="handleImageChange"
          />
          <small class="text-muted d-block mt-1">
            Image carrée légère recommandée. Idéalement une version PNG avec fond blanc ou sans fond, homogène avec les catégories précédentes.
            Taille maximale : 10 Mo.
          </small>
          <button
            v-if="imagePreviewUrl || form.image_url"
            type="button"
            class="btn btn-outline-secondary btn-sm mt-2"
            @click="clearImage"
          >
            <i class="fa fa-xmark me-1"></i>
            Retirer l’image
          </button>
        </div>
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label small fw-semibold">Description</label>
      <PrimevueTextarea
        class="w-100"
        rows="2"
        v-model="form.description"
        autoResize
        placeholder="Type de produits, remarques..."
      />
    </div>

    <div v-if="mode === 'create'" class="form-check d-flex align-items-center gap-2 ps-0 mb-3">
      <PrimevueCheckbox v-model="form.create_in_all_shops" binary inputId="categoryCreateInAllShops" />
      <label class="form-check-label small" for="categoryCreateInAllShops">
        Créer aussi dans tous les sites actifs
      </label>
    </div>

    <div class="d-flex justify-content-end gap-2 mt-3">
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        :disabled="submitting"
        @click="$emit('cancel')"
      >
        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
        Annuler
      </button>
      <AppLoadingButton
        type="button"
        class="btn btn-primary btn-sm"
        :loading="submitting"
        loading-label="Enregistrement..."
        @click="submit"
      >
        <i class="fa fa-save me-1"></i>
        Enregistrer
      </AppLoadingButton>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CategoryForm',
  props: {
    mode: {
      type: String,
      required: true, // 'create' | 'edit'
    },
    category: {
      type: Object,
      default: () => ({}),
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        id: undefined,
        name: '',
        code: '',
        position: 0,
        is_active: true,
        description: '',
        image_url: '',
        remove_image: false,
        create_in_all_shops: false,
      },
      imageFile: null,
      imagePreviewUrl: '',
    };
  },
  mounted() {
    this.initForm();
  },
  beforeUnmount() {
    this.revokePreviewUrl();
  },
  watch: {
    mode() {
      this.initForm();
    },
    category: {
      deep: true,
      handler() {
        this.initForm();
      },
    },
  },
  methods: {
    initForm() {
      this.revokePreviewUrl();
      this.imageFile = null;
      this.imagePreviewUrl = '';

      if (this.mode === 'edit' && this.category && this.category.id) {
        this.form.id = this.category.id;
        this.form.name = this.category.name || '';
        this.form.code = this.category.code || '';
        this.form.position = this.category.position ?? 0;
        this.form.is_active =
          typeof this.category.is_active === 'boolean'
            ? this.category.is_active
            : true;
        this.form.description = this.category.description || '';
        this.form.image_url = this.category.image_url || '';
        this.form.remove_image = false;
        this.form.create_in_all_shops = false;
      } else {
        this.form = {
          id: undefined,
          name: '',
          code: '',
          position: 0,
          is_active: true,
          description: '',
          image_url: '',
          remove_image: false,
          create_in_all_shops: false,
        };
      }

      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = '';
      }
    },

    handleImageChange(event) {
      const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
      this.revokePreviewUrl();
      this.imageFile = file;
      this.form.remove_image = false;
      this.imagePreviewUrl = file ? URL.createObjectURL(file) : '';
    },

    clearImage() {
      this.revokePreviewUrl();
      this.imageFile = null;
      this.imagePreviewUrl = '';
      this.form.image_url = '';
      this.form.remove_image = true;

      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = '';
      }
    },

    revokePreviewUrl() {
      if (this.imagePreviewUrl) {
        URL.revokeObjectURL(this.imagePreviewUrl);
      }
    },

    submit() {
      if (this.submitting) return;

      if (!this.form.name.trim()) {
        alert('Le nom de la catégorie est obligatoire.');
        return;
      }

      const payload = new FormData();
      payload.append('id', this.form.id ? String(this.form.id) : '');
      payload.append('name', this.form.name.trim());
      payload.append('code', this.form.code || '');
      payload.append('position', String(this.form.position ?? 0));
      payload.append('is_active', this.form.is_active ? '1' : '0');
      payload.append('description', this.form.description || '');

      if (this.mode === 'create') {
        payload.append('create_in_all_shops', this.form.create_in_all_shops ? '1' : '0');
      } else {
        payload.append('_method', 'PUT');
        payload.append('remove_image', this.form.remove_image ? '1' : '0');
      }

      if (this.imageFile) {
        payload.append('image', this.imageFile);
      }

      this.$emit('save', payload);
    },
  },
};
</script>

<style scoped>
.category-image-field {
  align-items: flex-start;
  display: flex;
  gap: 0.85rem;
}

.category-image-preview {
  align-items: center;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 0.75rem;
  display: inline-flex;
  flex: 0 0 auto;
  height: 5rem;
  justify-content: center;
  overflow: hidden;
  width: 5rem;
}

.category-image-preview img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}
</style>
